import { inject, Injectable } from "@angular/core";
import { ReplaySubject, Observable } from "rxjs";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import moment from "moment";

import { config } from "../site-local";
import { BackendService } from "./backend.service";
import { NotificationService } from "./notification.service";
import { Helpers } from "../utils/browser";
import {
    Attachment,
    ConsultationDate,
    ConsultationProcess,
    Division,
    Document,
    GlobalOccasionSection,
    OccasionOccasionEntry,
    SearchHint,
    SearchResult,
} from "../data/model";
export {
    Address,
    Agent,
    Attachment,
    Cancellation,
    Company,
    ConsultationDate,
    ConsultationProcess,
    ConsultationProcessState,
    CreateDocumentResult,
    Customer,
    Decision,
    Demand,
    Division,
    DivisionEntry,
    Document,
    Event,
    Exemption,
    FinalizeResult,
    FoundConsultationProcess,
    GlobalOccasion,
    GlobalOccasionEntry,
    GlobalOccasionSection,
    Hint,
    Individual,
    OccasionOccasionEntry,
    Offer,
    Participant,
    Person,
    ProductOccasionEntry,
    ProductOccasionSection,
    Protocol,
    Recommendation,
    SearchHint,
    SearchResult,
    SignatureFieldMetadata,
    Textblock,
    TrackingText,
    UpdateResult,
} from "../data/model";

import { isIndividual, isCompany } from "../data/model-utils";
import { PersistenceService } from "../store/model/persistence.service";
import { map, retry } from "rxjs/operators";
import { DomSanitizer } from "@angular/platform-browser";
export { isIndividual, isCompany };

@Injectable()
export class ConsultationService extends BackendService {
    serviceUrl: string = config.ZBDSERVICEURL;
    private consultationProcessSource =
        new ReplaySubject<ConsultationProcess>();
    consultationProcess$ = this.consultationProcessSource.asObservable();
    processLock: boolean;

    private cpLoadError: string;

    private dataStore: {
        consultationProcess: ConsultationProcess;
    };

    private sanitizer = inject(DomSanitizer);

    constructor(
        http: HttpClient,
        private notificationService: NotificationService,
        private persistence: PersistenceService,
    ) {
        super(http);
        this.dataStore = { consultationProcess: {} as ConsultationProcess };
    }

    loadFromBackend(consultationId: number): void {
        const url = `${this.serviceUrl}/ConsultationProcesses(${consultationId})?$expand=*($levels=20)`;
        this.http.get<ConsultationProcess>(url).subscribe(
            (data) => {
                this.dataStore.consultationProcess = data;
                this.logger.log("Loaded successfully");
                this.processLock =
                    this.dataStore.consultationProcess.IsFinished;
                this.cpLoadError = null;
                this.consultationProcessSource.next(
                    this.dataStore.consultationProcess,
                );
            },
            (error) => {
                this.logger.log(
                    "Could not load ConsultationProcess with ID %d: %s",
                    consultationId,
                    error,
                );
                this.notificationService.errorExt(
                    "Vorgang laden",
                    "Vorgang konnte nicht geladen werden.",
                    error,
                );
                this.cpLoadError = `CpId=${consultationId}, ${error}`;
            },
        );
    }

    getDocumentUrl(documentId: number) {
        return `${this.serviceUrl}/Documents(${documentId})/$value`;
    }

    loadDocument(doc: Document): Observable<Blob> {
        const url = this.getDocumentUrl(doc.Id);
        return this.loadPDF(url);
    }

    downloadDocument(doc: Document) {
        Helpers.downloadBlob(
            () =>
                this.loadDocument(doc)
                    .toPromise()
                    .then((blob) => ({
                        fileName: doc.Name,
                        data: blob,
                    })),
            (filename, blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a") as HTMLAnchorElement;
                link.href = url;
                link.download = filename;
                link.click();
                URL.revokeObjectURL(url);
                link.remove();
            },
        ).catch((error) => {
            this.notificationService.errorExt(
                "Dokument herunterladen",
                "Dokument konnte nicht heruntergeladen werden.",
                error,
            );
        });
    }

    sendEmail(consultationId: number, recipientEmail: string) {
        if (!consultationId) return;
        if (this.trace)
            this.log("POST", `/ConsultationProcesses(${consultationId})`);
        const url = `${this.serviceUrl}/ConsultationProcesses(${consultationId})/com.zurich.zbd.data.model.ConsultationProcess.sendEmail`;
        const args = {
            recipientEmail: recipientEmail,
        };
        return this.http.post(url, args, this.reqOpts);
    }

    searchProcesses(
        hint: SearchHint,
        page: number,
    ): Observable<SearchResult[]> {
        const url = `${this.serviceUrl}/searchConsultationProcess`;
        hint = Object.assign({}, hint);
        this.adjustDateFormats(hint);
        const args = {
            hint: hint,
            page: page,
        };
        return this.http
            .post<Record<string, SearchResult[]>>(url, args, this.reqOpts)
            .pipe(map((response) => response.value));
    }

    private adjustDateFormats(hint) {
        hint["Birthdate"] = this.formatDate(hint["Birthdate"]);
        hint["BeginCreationDate"] = this.formatDate(hint["BeginCreationDate"]);
        hint["EndCreationDate"] = this.formatDate(hint["EndCreationDate"]);
        hint["BeginFinalizationDate"] = this.formatDate(
            hint["BeginFinalizationDate"],
        );
        hint["EndFinalizationDate"] = this.formatDate(
            hint["EndFinalizationDate"],
        );
    }

    private formatDate(date) {
        if (date === null || date === "") {
            return null;
        } else {
            return moment(date).format("YYYY-MM-DD");
        }
    }

    get loadError(): string {
        return this.cpLoadError;
    }

    reloadFromBackend(): void {
        this.loadFromBackend(this.dataStore.consultationProcess.Id);
    }

    /*public addDivision_ = (division: Division, protocol: Protocol) => {
        let cp = this.dataStore.consultationProcess
        // FIXME zu welcher offer soll die Sparte hinzugefügt werden
        let url = `${this.serviceUrl}/Protocols(${protocol.Id})/Offers(${protocol.Offers[0].Id})/Divisions`
        return this.http.post(url, JSON.stringify(division), this.header).map(response => response.json());
    }*/

    private findDivisionByGroup(
        divisions: Division[],
        groupName: string,
    ): Division {
        for (const division of divisions) {
            if (division.GroupName === groupName) {
                return division;
            }
        }
        return undefined;
    }

    public updateConsultationDate(
        consultationDate: ConsultationDate,
    ): Observable<ConsultationDate> {
        const cp = this.dataStore.consultationProcess;
        const consultationDateDao = {
            Id: consultationDate.Id,
            Date: moment.utc(consultationDate.Date).format("YYYY-MM-DD"),
        };
        const url = `${this.serviceUrl}/ConsultationProcesses(${cp.Id})/ConsultationDates(${consultationDate.Id})`;
        return this.http.put<ConsultationDate>(
            url,
            JSON.stringify(consultationDateDao),
            this.reqOpts,
        );
    }

    public addOccasionOccasionEntries(
        entries: OccasionOccasionEntry[],
    ): Observable<any> {
        const cp = this.dataStore.consultationProcess;
        const args = {
            entries: {
                Entries: entries,
            },
        };
        const url = `${this.serviceUrl}/ConsultationProcesses(${cp.Id})/com.zurich.zbd.data.model.ConsultationProcess.addOccasionEntries`;
        return this.http.post(url, JSON.stringify(args), this.reqOpts);
    }

    public addDeepGlobalOccasionSection(
        globalOccasionSection: GlobalOccasionSection,
    ): Observable<any> {
        const cp = this.dataStore.consultationProcess;
        const args = {
            section: globalOccasionSection,
        };
        const url = `${this.serviceUrl}/ConsultationProcesses(${cp.Id})/com.zurich.zbd.data.model.ConsultationProcess.addGlobalOccasionSection?$expand=*($levels=20)`;
        return this.http.post(url, JSON.stringify(args), this.reqOpts);
    }

    public finalizeConsultationProcess(
        cp: ConsultationProcess,
    ): Observable<ConsultationProcess> {
        const url = `${this.serviceUrl}/ConsultationProcesses(${cp.Id})/com.zurich.zbd.data.model.ConsultationProcess.finalizeConsultationProcess?$expand=Document`;
        const args = {};
        return this.http.post<ConsultationProcess>(url, args, this.reqOpts);
    }

    public signAndFinalizeConsultationProcess(
        cp: ConsultationProcess,
        signatures,
    ): Observable<ConsultationProcess> {
        const url = `${this.serviceUrl}/ConsultationProcesses(${cp.Id})/com.zurich.zbd.data.model.ConsultationProcess.signAndFinalizeConsultationProcess?$expand=Document`;
        const args = {
            signatures: signatures,
        };
        return this.http.post<ConsultationProcess>(
            url,
            JSON.stringify(args),
            this.reqOpts,
        );
    }

    public uploadAttachmentData(
        attachment: Attachment,
        content: Blob,
    ): Observable<any> {
        const url = `${this.serviceUrl}/Attachments(${attachment.Id})/$value`;
        return this.http.put(url, content);
    }

    public getAttachmentUrl(attachmentId: number) {
        return `${this.serviceUrl}/Attachments(${attachmentId})/$value`;
    }

    public loadAttachment(attachment: Attachment): Observable<Blob> {
        const url = this.getAttachmentUrl(attachment.Id);
        return this.loadPDF(url);
    }

    public downloadAttachment(attachment: Attachment) {
        Helpers.downloadBlob(
            () =>
                this.loadAttachment(attachment)
                    .toPromise()
                    .then((blob) => ({
                        fileName: attachment.Name,
                        data: blob,
                    })),
            (filename, blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a") as HTMLAnchorElement;
                link.href = url;
                link.download = filename;
                link.click();
                URL.revokeObjectURL(url);
                link.remove();
            },
        ).catch(() => {
            this.notificationService.error(
                "Anhang herunterladen",
                "Anhang konnte nicht heruntergeladen werden.",
            );
            this.persistence.deleteAttachment(attachment);
        });
    }

    public loadPDF(url: string): Observable<Blob> {
        return this.http
            .get(url, {
                headers: new HttpHeaders({
                    "Content-Type": "application/pdf",
                }),
                responseType: "blob",
            })
            .pipe(
                retry(2),
                map((blob) => new Blob([blob], { type: "application/pdf" })),
            );
    }

    public getFinishedProcessIds(page = 0): Observable<number[]> {
        const url = `${this.serviceUrl}/getFinishedProcessIds(page=${page})`;
        return this.http
            .get<{ value: number[] }>(url)
            .pipe(map((response) => response.value));
    }

    public getDocumentByConsultationProcessId(
        consulatationProcessId: number,
    ): Observable<Document> {
        if (!consulatationProcessId) return;
        const url = `${this.serviceUrl}/ConsultationProcesses(${consulatationProcessId})/Document`;
        return this.http.get<Document>(url, this.reqOpts);
    }
}

export interface Validity {
    Key: string;
    Value: boolean;
}
